import { Button, Spinner, Text, Tooltip } from "@fluentui/react-components";
import {
  ArrowDownload16Regular,
  ChevronLeft16Regular,
} from "@fluentui/react-icons";
import { ATD, ImpactAnalysis } from "apps/decooda/DecoodaReviews";
import { DialogTabs } from "components/dialogTabs/DialogTabs";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { exportProject, getProject } from "../../decoodaapi";
import { IProject } from "./projectCard.types";
import { useProjectManagementPageStyles } from "./projectManagementPage.styles";
import { TAB_KEYS, TabValue } from "./ProjectManagementPage.types";

export const ProjectManagementPage = (): JSX.Element => {
  const styles = useProjectManagementPageStyles();
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();

  const [project, setProject] = useState<IProject | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState<TabValue>(
    TAB_KEYS.AUTO_TOPIC_DISCOVERY,
  );
  const [startATDDownload, setStartATDDownload] = useState<boolean>(false);
  const [startImpactAnalysisDownload, setStartImpactAnalysisDownload] =
    useState<boolean>(false);
  const [isProjectExporting, setIsProjectExporting] = useState<boolean>(false);

  const fetchProject = useCallback(async () => {
    if (!projectId) return;

    try {
      setIsLoading(true);
      const data = await getProject({ projectId });
      setProject(data);
    } catch (error) {
      console.error("Error fetching project:", error);
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  const handleBackClick = () => {
    navigate("/ai-analytics");
  };

  const getDownloadTooltip = useCallback((): string => {
    if (startATDDownload || startImpactAnalysisDownload) {
      return "Export in progress";
    }

    if (selectedTab === TAB_KEYS.AUTO_TOPIC_DISCOVERY) {
      return "Export Topics Results";
    }

    if (selectedTab === TAB_KEYS.IMPACT_ANALYSIS) {
      return "Export Impact Analysis Results";
    }

    return "Export results";
  }, [startATDDownload, startImpactAnalysisDownload, selectedTab]);

  const handleProjectExport = async () => {
    if (!projectId) return;
    setIsProjectExporting(true);
    try {
      await exportProject({ projectId });
    } catch (error) {
      console.error("Error exporting project:", error);
    } finally {
      setIsProjectExporting(false);
    }
  };

  const onDownloadClick = () => {
    if (selectedTab === TAB_KEYS.AUTO_TOPIC_DISCOVERY) {
      setStartATDDownload(true);
    } else if (selectedTab === TAB_KEYS.IMPACT_ANALYSIS) {
      setStartImpactAnalysisDownload(true);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" label="Loading project..." />
      </div>
    );
  }

  if (!project) {
    return (
      <div className={styles.container}>
        <div className={styles.headerRow}>
          <Button
            appearance="outline"
            shape="rounded"
            icon={<ChevronLeft16Regular />}
            onClick={handleBackClick}
            aria-label="Back to Projects"
          />
        </div>
        <span className={styles.notFoundText}>Project not found.</span>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <Button
          appearance="outline"
          shape="rounded"
          icon={<ChevronLeft16Regular />}
          onClick={handleBackClick}
          aria-label="Back to Projects"
        />
        <Text size={500} weight="semibold" className={styles.title}>
          {project.name}
        </Text>
        <Tooltip
          content={
            isProjectExporting ? "Export in progress" : "Download Project Data"
          }
          relationship="label"
        >
          <Button
            appearance="outline"
            shape="rounded"
            style={{ marginLeft: "auto" }}
            icon={
              isProjectExporting ? (
                <Spinner size="tiny" />
              ) : (
                <ArrowDownload16Regular />
              )
            }
            onClick={handleProjectExport}
            disabled={isProjectExporting}
            aria-label="Download Project Data"
          >
            Project Data
          </Button>
        </Tooltip>
      </div>

      <div className={styles.sectionScrollArea}>
        <div className={styles.tabRow}>
          <DialogTabs
            tabs={[
              {
                key: TAB_KEYS.AUTO_TOPIC_DISCOVERY,
                label: "Auto Topic Discovery",
              },
              { key: TAB_KEYS.IMPACT_ANALYSIS, label: "Impact Analysis" },
            ]}
            activeTab={selectedTab}
            onTabClick={(tab) => setSelectedTab(tab as TabValue)}
          />
          <Tooltip content={getDownloadTooltip()} relationship="label">
            <Button
              appearance="outline"
              shape="rounded"
              icon={
                startATDDownload || startImpactAnalysisDownload ? (
                  <Spinner size="tiny" />
                ) : (
                  <ArrowDownload16Regular />
                )
              }
              onClick={onDownloadClick}
              aria-label={getDownloadTooltip()}
              disabled={startATDDownload || startImpactAnalysisDownload}
            />
          </Tooltip>
        </div>

        <div className={styles.tabContentArea}>
          {selectedTab === TAB_KEYS.AUTO_TOPIC_DISCOVERY && projectId && (
            <ATD
              projectId={projectId}
              projectName={project?.name}
              startDownload={startATDDownload}
              setStartDownload={setStartATDDownload}
            />
          )}

          {selectedTab === TAB_KEYS.IMPACT_ANALYSIS && projectId && (
            <ImpactAnalysis
              projectId={projectId}
              projectName={project?.name}
              startDownload={startImpactAnalysisDownload}
              setStartDownload={setStartImpactAnalysisDownload}
            />
          )}
        </div>
      </div>
    </div>
  );
};
